// The football stat catalog. Keys are ours, not the provider's; the adapter
// maps provider fields onto these at the boundary.
//
// `scoring` is how a scoring table says each stat to somebody who has never
// played (PLAN-2026-10-18-scoring-anyone-can-read §3): a row's label, the
// plural for "every 25 passing yards", the units a league may count it in
// (ESPN's: every 1, 5, 10, 20, 25, 50 or 100 yards; every 1, 5 or 10 of a
// count), and the words an experienced player searches with.

import type { StatDef, StatScoringWords } from '@fsp/sports-core';

const OFF = ['QB', 'RB', 'WR', 'TE'] as const;
const IDP = ['DL', 'LB', 'DB'] as const;

/** ESPN's choices for yardage. */
const YARDS = [1, 5, 10, 20, 25, 50, 100] as const;
/** ESPN's choices for a count a league might price by the handful. */
const COUNTS = [1, 5, 10] as const;

function s(
  key: string,
  name: string,
  shortName: string,
  category: string,
  extra: Partial<StatDef> = {},
): StatDef {
  return {
    key,
    name,
    shortName,
    category,
    kind: 'count',
    scorable: true,
    categorizable: false,
    decimals: 0,
    ...extra,
  };
}

function w(label: string, plural: string, more: Partial<StatScoringWords> = {}): StatScoringWords {
  return { label, plural, ...more };
}

const yards = (unit: number, aliases: string[] = []) => ({
  units: YARDS,
  unit,
  ...(aliases.length ? { aliases } : {}),
});

export const footballStats: readonly StatDef[] = [
  // Passing
  s('pass_att', 'Pass Attempts', 'Att', 'passing', {
    positions: [...OFF],
    scoring: w('Each pass thrown', 'passes thrown', { units: COUNTS, aliases: ['attempts'] }),
  }),
  s('pass_cmp', 'Completions', 'Cmp', 'passing', {
    positions: [...OFF],
    scoring: w('Each completion', 'completions', { units: COUNTS }),
  }),
  s('pass_inc', 'Incompletions', 'Inc', 'passing', {
    positions: [...OFF],
    scoring: w('Each incompletion', 'incompletions', { units: COUNTS }),
  }),
  s('pass_yd', 'Passing Yards', 'Pass Yd', 'passing', {
    kind: 'yards',
    positions: [...OFF],
    scoring: w('Each passing yard', 'passing yards', yards(25, ['PY', 'pass yards'])),
  }),
  s('pass_td', 'Passing Touchdowns', 'Pass TD', 'passing', {
    positions: [...OFF],
    scoring: w('Touchdown pass', 'touchdown passes', { aliases: ['pass TD', 'PTD'] }),
  }),
  s('pass_int', 'Interceptions Thrown', 'INT', 'passing', {
    positions: [...OFF],
    scoring: w('Interception thrown', 'interceptions thrown', { aliases: ['INT', 'pick'] }),
  }),
  s('pass_2pt', 'Passing 2-Point Conversions', 'Pass 2PT', 'passing', {
    positions: [...OFF],
    scoring: w('2-point pass', '2-point passes', { aliases: ['2PT', 'two-point'] }),
  }),
  s('pass_sack', 'Sacks Taken', 'Sacked', 'passing', {
    positions: [...OFF],
    scoring: w('Sacked', 'times sacked'),
  }),
  s('pass_fd', 'Passing First Downs', 'Pass 1D', 'passing', {
    positions: [...OFF],
    scoring: w('Passing first down', 'passing first downs'),
  }),
  // Rushing
  s('rush_att', 'Rush Attempts', 'Car', 'rushing', {
    positions: [...OFF],
    scoring: w('Each carry', 'carries', { units: COUNTS, aliases: ['rush attempts'] }),
  }),
  s('rush_yd', 'Rushing Yards', 'Rush Yd', 'rushing', {
    kind: 'yards',
    positions: [...OFF],
    scoring: w('Each rushing yard', 'rushing yards', yards(10, ['RY', 'rush yards'])),
  }),
  s('rush_td', 'Rushing Touchdowns', 'Rush TD', 'rushing', {
    positions: [...OFF],
    scoring: w('Rushing touchdown', 'rushing touchdowns', { aliases: ['rush TD'] }),
  }),
  s('rush_2pt', 'Rushing 2-Point Conversions', 'Rush 2PT', 'rushing', {
    positions: [...OFF],
    scoring: w('2-point run', '2-point runs', { aliases: ['2PT', 'two-point'] }),
  }),
  s('rush_fd', 'Rushing First Downs', 'Rush 1D', 'rushing', {
    positions: [...OFF],
    scoring: w('Rushing first down', 'rushing first downs'),
  }),
  // Receiving
  s('rec_tgt', 'Targets', 'Tgt', 'receiving', {
    positions: [...OFF],
    scoring: w('Each target', 'targets', { units: COUNTS }),
  }),
  s('rec', 'Receptions', 'Rec', 'receiving', {
    positions: [...OFF],
    scoring: w('Each catch', 'catches', { units: COUNTS, aliases: ['PPR', 'reception', 'rec'] }),
  }),
  s('rec_yd', 'Receiving Yards', 'Rec Yd', 'receiving', {
    kind: 'yards',
    positions: [...OFF],
    scoring: w('Each receiving yard', 'receiving yards', yards(10, ['REY', 'rec yards'])),
  }),
  s('rec_td', 'Receiving Touchdowns', 'Rec TD', 'receiving', {
    positions: [...OFF],
    scoring: w('Receiving touchdown', 'receiving touchdowns', { aliases: ['rec TD'] }),
  }),
  s('rec_2pt', 'Receiving 2-Point Conversions', 'Rec 2PT', 'receiving', {
    positions: [...OFF],
    scoring: w('2-point catch', '2-point catches', { aliases: ['2PT', 'two-point'] }),
  }),
  s('rec_fd', 'Receiving First Downs', 'Rec 1D', 'receiving', {
    positions: [...OFF],
    scoring: w('Receiving first down', 'receiving first downs'),
  }),
  // Misc offense
  s('fum', 'Fumbles', 'Fum', 'misc', {
    positions: [...OFF],
    scoring: w('Any fumble, lost or not', 'fumbles'),
  }),
  s('fum_lost', 'Fumbles Lost', 'Fum L', 'misc', {
    positions: [...OFF],
    scoring: w('Fumble lost', 'fumbles lost', { aliases: ['fumble'] }),
  }),
  s('fum_rec_td', 'Fumble Recovery Touchdowns', 'FR TD', 'misc', {
    positions: [...OFF],
    scoring: w('Fumble recovered for a touchdown', 'fumble-recovery touchdowns'),
  }),
  // Returns are a defensive back's too (the review of Q11's backend): a
  // stat scores whoever makes it, and his table shows it under his "more".
  s('ret_yd', 'Return Yards', 'Ret Yd', 'misc', {
    kind: 'yards',
    positions: [...OFF, ...IDP],
    scoring: w('Each kick or punt return yard', 'return yards', {
      units: [1, 10, 25],
      unit: 25,
    }),
  }),
  s('ret_td', 'Return Touchdowns', 'Ret TD', 'misc', {
    positions: [...OFF, ...IDP],
    scoring: w('Kick or punt return touchdown', 'return touchdowns', { aliases: ['KR', 'PR'] }),
  }),
  // Kicking
  s('fg_0_19', 'Field Goals 0–19', 'FG 0-19', 'kicking', {
    positions: ['K'],
    scoring: w('Field goal, 0–19 yards', 'field goals of 0–19 yards', {
      range: { series: 'fg', min: 0, max: 19 },
      aliases: ['FG'],
    }),
  }),
  s('fg_20_29', 'Field Goals 20–29', 'FG 20-29', 'kicking', {
    positions: ['K'],
    scoring: w('Field goal, 20–29 yards', 'field goals of 20–29 yards', {
      range: { series: 'fg', min: 20, max: 29 },
      aliases: ['FG'],
    }),
  }),
  s('fg_30_39', 'Field Goals 30–39', 'FG 30-39', 'kicking', {
    positions: ['K'],
    scoring: w('Field goal, 30–39 yards', 'field goals of 30–39 yards', {
      range: { series: 'fg', min: 30, max: 39 },
      aliases: ['FG'],
    }),
  }),
  s('fg_40_49', 'Field Goals 40–49', 'FG 40-49', 'kicking', {
    positions: ['K'],
    scoring: w('Field goal, 40–49 yards', 'field goals of 40–49 yards', {
      range: { series: 'fg', min: 40, max: 49 },
      aliases: ['FG'],
    }),
  }),
  s('fg_50p', 'Field Goals 50+', 'FG 50+', 'kicking', {
    positions: ['K'],
    scoring: w('Field goal, 50 yards or more', 'field goals of 50 yards or more', {
      range: { series: 'fg', min: 50, max: null },
      aliases: ['FG'],
    }),
  }),
  s('fg_miss', 'Field Goals Missed', 'FG Miss', 'kicking', {
    positions: ['K'],
    scoring: w('Missed field goal', 'missed field goals', { aliases: ['FG'] }),
  }),
  s('xp_made', 'Extra Points Made', 'XP', 'kicking', {
    positions: ['K'],
    scoring: w('Extra point', 'extra points', { aliases: ['PAT', 'XP'] }),
  }),
  s('xp_miss', 'Extra Points Missed', 'XP Miss', 'kicking', {
    positions: ['K'],
    scoring: w('Missed extra point', 'missed extra points', { aliases: ['PAT', 'XP'] }),
  }),
  // Team defense / special teams
  s('dst_sack', 'Sacks', 'Sack', 'defense', {
    positions: ['DST'],
    scoring: w('Sack', 'sacks'),
  }),
  s('dst_int', 'Interceptions', 'INT', 'defense', {
    positions: ['DST'],
    scoring: w('Interception', 'interceptions', { aliases: ['INT', 'pick', 'takeaway'] }),
  }),
  s('dst_fum_rec', 'Fumble Recoveries', 'FR', 'defense', {
    positions: ['DST'],
    scoring: w('Fumble recovery', 'fumble recoveries', { aliases: ['takeaway'] }),
  }),
  s('dst_ff', 'Forced Fumbles', 'FF', 'defense', {
    positions: ['DST'],
    scoring: w('Forced fumble', 'forced fumbles'),
  }),
  s('dst_td', 'Defensive Touchdowns', 'Def TD', 'defense', {
    positions: ['DST'],
    scoring: w('Defensive touchdown', 'defensive touchdowns'),
  }),
  s('dst_safety', 'Safeties', 'Sfty', 'defense', {
    positions: ['DST'],
    scoring: w('Safety', 'safeties'),
  }),
  s('dst_blk', 'Blocked Kicks', 'Blk', 'defense', {
    positions: ['DST'],
    scoring: w('Blocked kick', 'blocked kicks'),
  }),
  s('dst_ret_td', 'Return Touchdowns', 'Ret TD', 'defense', {
    positions: ['DST'],
    scoring: w('Kick or punt return touchdown', 'return touchdowns', { aliases: ['KR', 'PR'] }),
  }),
  s('dst_pts_allowed', 'Points Allowed', 'PA', 'defense', {
    kind: 'tiered',
    positions: ['DST'],
    // The usual numbers' bands, so a set worth nothing can be brought back.
    scoring: w('Points allowed', 'points allowed', {
      bands: {
        noun: 'points allowed',
        zero: 'Shutout',
        defaults: [
          { min: 0, max: 0 },
          { min: 1, max: 6 },
          { min: 7, max: 13 },
          { min: 14, max: 20 },
          { min: 21, max: 27 },
          { min: 28, max: 34 },
          { min: 35, max: null },
        ],
      },
      aliases: ['PA'],
    }),
  }),
  s('dst_yds_allowed', 'Yards Allowed', 'YA', 'defense', {
    kind: 'tiered',
    positions: ['DST'],
    // Sleeper's bands, which are ESPN's from 300 up.
    scoring: w('Yards allowed', 'yards allowed', {
      bands: {
        noun: 'yards allowed',
        defaults: [
          { min: 0, max: 99 },
          { min: 100, max: 199 },
          { min: 200, max: 299 },
          { min: 300, max: 349 },
          { min: 350, max: 399 },
          { min: 400, max: 449 },
          { min: 450, max: 499 },
          { min: 500, max: 549 },
          { min: 550, max: null },
        ],
      },
      aliases: ['YA'],
    }),
  }),
  // Defensive players (Q11, PLAN-2026-10-18-defensive-players §2). A
  // defender's own line, from nflverse's `def_*` columns on his row; the team
  // defense's `dst_*` keys are its club's line and never his.
  ...[
    [
      'idp_tkl_solo',
      'Solo Tackles',
      'Solo',
      w('Solo tackle', 'solo tackles', { aliases: ['tackle', 'TKL'] }),
    ],
    [
      'idp_tkl_ast',
      'Assisted Tackles',
      'Ast',
      w('Assisted tackle', 'assisted tackles', { aliases: ['tackle', 'assist'] }),
    ],
    [
      'idp_tkl_loss',
      'Tackles for Loss',
      'TFL',
      w('Tackle for a loss', 'tackles for a loss', { aliases: ['TFL', 'tackle'] }),
    ],
    ['idp_sack', 'Sacks', 'Sack', w('Sack', 'sacks')],
    [
      'idp_qb_hit',
      'QB Hits',
      'QB Hit',
      w('Hit on the quarterback', 'quarterback hits', { aliases: ['QB hit'] }),
    ],
    [
      'idp_int',
      'Interceptions',
      'INT',
      w('Interception', 'interceptions', { aliases: ['INT', 'pick'] }),
    ],
    [
      'idp_pass_def',
      'Passes Defended',
      'PD',
      w('Pass defended', 'passes defended', { aliases: ['PD', 'pass breakup'] }),
    ],
    ['idp_ff', 'Forced Fumbles', 'FF', w('Forced fumble', 'forced fumbles')],
    ['idp_fum_rec', 'Fumble Recoveries', 'FR', w('Fumble recovery', 'fumble recoveries')],
    ['idp_td', 'Defensive Touchdowns', 'Def TD', w('Defensive touchdown', 'defensive touchdowns')],
    ['idp_safety', 'Safeties', 'Sfty', w('Safety', 'safeties')],
    ['idp_blk', 'Blocked Kicks', 'Blk', w('Blocked kick', 'blocked kicks')],
  ].map(([key, name, shortName, scoring]) =>
    s(key as string, name as string, shortName as string, 'idp', {
      positions: [...IDP],
      ...(key === 'idp_sack' || key === 'idp_tkl_loss' ? { decimals: 1 } : {}),
      scoring: scoring as StatScoringWords,
    }),
  ),
  // Research-only (never scored): from nflverse
  s('snap_pct', 'Snap Share', 'Snap%', 'research', {
    scorable: false,
    decimals: 1,
    positions: [...OFF],
  }),
  s('target_share', 'Target Share', 'Tgt%', 'research', {
    scorable: false,
    decimals: 1,
    positions: [...OFF],
  }),
  s('rz_touches', 'Red Zone Touches', 'RZ', 'research', { scorable: false, positions: [...OFF] }),
  s('air_yd', 'Air Yards', 'Air Yd', 'research', {
    kind: 'yards',
    scorable: false,
    positions: [...OFF],
  }),
  // A catch's yards after it was made, read since 2026-11-01 for the weekly
  // challenge (Q9), which can score a receiver on it.
  s('rec_yac', 'Yards After Catch', 'YAC', 'research', {
    kind: 'yards',
    scorable: false,
    positions: [...OFF],
  }),
  s('opp_rank_vs_pos', 'Opponent Rank vs Position', 'Opp Rk', 'research', { scorable: false }),
];
